// src/components/PreviewModal.js

import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-modal';
import { PdfViewer } from '../viewer/PdfViewer';
import { 
  FaSearchPlus, 
  FaSearchMinus, 
  FaRedo, 
  FaSyncAlt, 
  FaDownload, 
  FaFileExcel, 
  FaFileWord, 
  FaFilePowerpoint, 
  FaFileArchive, 
  FaFileAlt, 
  FaLock 
} from 'react-icons/fa';
import './PreviewModal.css';

Modal.setAppElement('#root');

const getFileExtension = (fileName) => {
  if (!fileName || !fileName.includes('.')) return '';
  return fileName.split('.').pop().toLowerCase();
};

const ImagePreviewer = ({ fileUrl, fileName, allowDownload = true }) => {
  const [scale, setScale] = useState(1.0);
  const [rotate, setRotate] = useState(0);
  const [backdrop, setBackdrop] = useState('grid'); // grid | dark | light
  const stageRef = useRef(null);

  const handleZoomIn = () => setScale(s => Math.min(s + 0.25, 4.0));
  const handleZoomOut = () => setScale(s => Math.max(s - 0.25, 0.25));
  const handleRotate = () => setRotate(r => (r + 90) % 360);
  const handleReset = () => {
    setScale(1.0);
    setRotate(0);
  };

  return (
    <div className="image-viewer-container">
      <div className="image-toolbar">
        <div className="image-toolbar-group">
          <button className="image-toolbar-btn" onClick={handleZoomOut} disabled={scale <= 0.25} title="Zoom Out">
            <FaSearchMinus />
          </button>
          <span className="image-zoom-text">{Math.round(scale * 100)}%</span>
          <button className="image-toolbar-btn" onClick={handleZoomIn} disabled={scale >= 4.0} title="Zoom In">
            <FaSearchPlus />
          </button>
          <div className="image-toolbar-divider" />
          <button className="image-toolbar-btn" onClick={handleRotate} title="Rotate Clockwise">
            <FaRedo />
          </button>
          <button className="image-toolbar-btn" onClick={handleReset} title="Reset View">
            <FaSyncAlt />
          </button>
        </div>
        <div className="image-toolbar-group">
          <button 
            className={`image-toolbar-btn ${backdrop === 'grid' ? 'active' : ''}`} 
            onClick={() => setBackdrop('grid')} 
            title="Grid Background"
          >
            Grid
          </button>
          <button 
            className={`image-toolbar-btn ${backdrop === 'dark' ? 'active' : ''}`} 
            onClick={() => setBackdrop('dark')} 
            title="Dark Background"
          >
            Dark
          </button>
          <button 
            className={`image-toolbar-btn ${backdrop === 'light' ? 'active' : ''}`} 
            onClick={() => setBackdrop('light')} 
            title="Light Background"
          >
            Light
          </button>
          {allowDownload && (
            <>
              <div className="image-toolbar-divider" />
              <a href={fileUrl} download={fileName} className="image-toolbar-btn" title="Download Image">
                <FaDownload /> Download
              </a>
            </>
          )}
        </div>
      </div>
      <div className={`image-stage ${backdrop}-backdrop`} ref={stageRef}>
        <img 
          src={fileUrl} 
          alt={fileName} 
          className="preview-image-element shadowed"
          style={{ transform: `scale(${scale}) rotate(${rotate}deg)` }}
        />
      </div>
    </div>
  );
};

const TextViewer = ({ fileUrl, fileName }) => {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(fileUrl)
      .then(res => {
        if (!res.ok) throw new Error("Could not fetch text content");
        return res.text();
      })
      .then(text => {
        setContent(text);
        setLoading(false);
      })
      .catch(err => {
        setContent(`Error loading text preview: ${err.message}`);
        setLoading(false);
      });
  }, [fileUrl]);

  const handleCopy = () => {
    navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="text-viewer-container">
      <div className="text-viewer-toolbar">
        <span className="text-viewer-title">{fileName} (Text Preview)</span>
        <button className="image-toolbar-btn" onClick={handleCopy} title="Copy Content">
          {copied ? "Copied!" : "Copy Text"}
        </button>
      </div>
      {loading ? (
        <div className="pdf-loading-overlay">
          <div className="pdf-spinner" />
          <span>Loading content...</span>
        </div>
      ) : (
        <pre className="text-viewer-content">{content}</pre>
      )}
    </div>
  );
};

const UnsupportedFallback = ({ file, allowDownload = true }) => {
  const extension = getFileExtension(file.fileName);
  
  let themeClass = 'generic';
  let typeLabel = `${extension.toUpperCase()} File`;
  let Icon = FaFileAlt;

  if (['xlsx', 'xls', 'csv'].includes(extension)) {
    themeClass = 'xlsx';
    typeLabel = 'Excel Spreadsheet';
    Icon = FaFileExcel;
  } else if (['docx', 'doc'].includes(extension)) {
    themeClass = 'docx';
    typeLabel = 'Word Document';
    Icon = FaFileWord;
  } else if (['pptx', 'ppt'].includes(extension)) {
    themeClass = 'pptx';
    typeLabel = 'PowerPoint Presentation';
    Icon = FaFilePowerpoint;
  } else if (['zip', 'rar', '7z', 'tar', 'gz'].includes(extension)) {
    themeClass = 'archive';
    typeLabel = 'Compressed Archive';
    Icon = FaFileArchive;
  }

  return (
    <div className="fallback-card-container">
      <div className={`glass-fallback-card ${themeClass}`}>
        <div className="icon-wrapper">
          <Icon />
        </div>
        <span className="fallback-badge">{typeLabel}</span>
        <h3 className="fallback-card-title">{file.fileName}</h3>
        <p className="fallback-card-desc">
          Previews for Office documents and Zip archives are optimized for native applications. 
          {allowDownload ? " You can download this file directly to view or edit it." : ""}
        </p>
        
        {allowDownload ? (
          <a href={file.fileUrl} download={file.fileName} className="download-action-btn">
            <FaDownload /> Download File
          </a>
        ) : (
          <div className="secure-lock-badge">
            <FaLock /> Access Secured (Download Disabled)
          </div>
        )}
      </div>
    </div>
  );
};

export const PreviewContent = ({ file, allowDownload = true }) => {
  const extension = getFileExtension(file.fileName);
  const displayUrl = file.fileUrl;

  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension)) {
    return <ImagePreviewer fileUrl={displayUrl} fileName={file.fileName} allowDownload={allowDownload} />;
  }

  if (extension === 'pdf') {
    return <PdfViewer fileUrl={displayUrl} allowDownload={allowDownload} />;
  }

  if (['txt', 'csv', 'json', 'md', 'js', 'css', 'html', 'py', 'go', 'sh', 'sql'].includes(extension)) {
    return <TextViewer fileUrl={displayUrl} fileName={file.fileName} />;
  }

  return <UnsupportedFallback file={file} allowDownload={allowDownload} />;
};

export const PreviewModal = ({ isOpen, onRequestClose, file }) => {
  if (!file) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="File Preview Modal"
      className="modal"
      overlayClassName="overlay"
      style={{
        content: {
          maxWidth: '1000px',
          width: '95%',
          padding: '0',
          background: '#121214',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          borderRadius: '24px',
          boxShadow: '0 30px 80px rgba(0, 0, 0, 0.5)'
        }
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 24px', borderBottom: '1px solid rgba(255, 255, 255, 0.08)' }}>
        <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#ffffff', fontFamily: 'Outfit, sans-serif' }}>
          {file.fileName}
        </h2>
        <button 
          className="modal-close-btn" 
          onClick={onRequestClose} 
          aria-label="Close preview"
          style={{ position: 'static', transform: 'none', background: 'rgba(255,255,255,0.05)', width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyItems: 'center', justifyContent: 'center', borderRadius: '50%', color: '#ffffff', border: 'none', cursor: 'pointer' }}
        >
          &times;
        </button>
      </div>

      <div style={{ padding: '0px' }}>
        <PreviewContent file={file} allowDownload={true} />
      </div>
    </Modal>
  );
};